/** A workspace bot's robot avatar style and accent colour. The value sets
 * mirror the agents_avatar_*_check constraints. A bot with no saved choice is
 * drawn from a style and colour derived from its user id, so the chat (which
 * only knows the sender's user id) and settings always agree. Chosen
 * appearances are remembered here whenever the bots list loads, so chat rows
 * can draw the right face without threading bots through every view. */
import { useSyncExternalStore } from 'react';

export const BOT_AVATAR_STYLES = ['classic', 'round', 'visor', 'antenna', 'screen', 'pod'] as const;
export const BOT_ACCENT_COLORS = ['blue', 'teal', 'green', 'amber', 'orange', 'red', 'pink', 'purple', 'indigo', 'graphite'] as const;
export type BotAvatarStyle = typeof BOT_AVATAR_STYLES[number];
export type BotAccentColor = typeof BOT_ACCENT_COLORS[number];
export interface BotAppearance { style: BotAvatarStyle; color: BotAccentColor }

/** Apple system colours, so bots sit naturally beside the rest of the UI. */
export const BOT_ACCENT_HEX: Record<BotAccentColor, string> = {
  blue: '#0A84FF', teal: '#30B0C7', green: '#34C759', amber: '#FFB800', orange: '#FF9500',
  red: '#FF3B30', pink: '#FF2D55', purple: '#AF52DE', indigo: '#5856D6', graphite: '#8E8E93',
};
export const BOT_ACCENT_LABELS: Record<BotAccentColor, string> = {
  blue: 'Blue', teal: 'Teal', green: 'Green', amber: 'Amber', orange: 'Orange',
  red: 'Red', pink: 'Pink', purple: 'Purple', indigo: 'Indigo', graphite: 'Graphite',
};
export const BOT_STYLE_LABELS: Record<BotAvatarStyle, string> = {
  classic: 'Classic', round: 'Round', visor: 'Visor', antenna: 'Antenna', screen: 'Screen', pod: 'Pod',
};

/** FNV-1a: stable across sessions and identical in the Apple app. */
function hash(seed: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) { h ^= seed.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
  return h >>> 0;
}

export function defaultBotAppearance(seed: string): BotAppearance {
  const h = hash(seed);
  return { style: BOT_AVATAR_STYLES[h % BOT_AVATAR_STYLES.length], color: BOT_ACCENT_COLORS[(h >>> 8) % BOT_ACCENT_COLORS.length] };
}

export function resolveBotAppearance(bot: { id: string; userId?: string | null; avatarStyle?: BotAvatarStyle | null; avatarColor?: BotAccentColor | null }): BotAppearance {
  const fallback = defaultBotAppearance(bot.userId ?? bot.id);
  return { style: bot.avatarStyle ?? fallback.style, color: bot.avatarColor ?? fallback.color };
}

// ── Chosen appearances by bot user id ────────────────────────────────────────
const known = new Map<string, BotAppearance>();
/** The updatedAt each cached look was read at: a list fetched before a save
 * can land after it, and must not put the old look back. */
const readAt = new Map<string, string>();
const listeners = new Set<() => void>();
let version = 0;

export function rememberBotAppearances(bots: Array<Parameters<typeof resolveBotAppearance>[0] & { updatedAt?: string }>): void {
  let changed = false;
  for (const bot of bots) {
    if (!bot.userId) continue;
    const stamp = bot.updatedAt ?? '';
    const seen = readAt.get(bot.userId);
    if (seen && stamp && stamp < seen) continue;
    if (stamp) readAt.set(bot.userId, stamp);
    const next = resolveBotAppearance(bot);
    const previous = known.get(bot.userId);
    if (previous?.style === next.style && previous.color === next.color) continue;
    known.set(bot.userId, next);
    changed = true;
  }
  if (!changed) return;
  version += 1;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener); }; }

/** The appearance for a bot sender in chat, re-rendering if the bots list later loads a saved choice. */
export function useBotAppearance(botUserId: string | null | undefined): BotAppearance | null {
  useSyncExternalStore(subscribe, () => version, () => version);
  if (!botUserId) return null;
  return known.get(botUserId) ?? defaultBotAppearance(botUserId);
}
